import LBox from "@components/LBox";
import LText from "@components/LText";
import { FloatingFeedback } from "@components/FloatingFeedback";
import { Pulse } from "@components/Pulse";
import React from "react";
import { FloatingImage } from "./LevelBadge";
import usePrevious from "@hooks/usePrevious";
import { fonts, scaleFont } from "@theme";

interface StatsTrioProps {
  totalStreams: number;
  accesses: number;
}

// Design colors from Figma
const CYAN_GRADIENT = ["#56CCF2", "#2F80ED"];
const ORANGE_GRADIENT_NUMBER = ["#E33B02", "#F87C17"];
const ORANGE_GRADIENT_TEXT = ["#F38F38", "#FF5722"];

const formatNumber = (num: number): string => {
  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";

  if (
    abs >= 1_000_000_000 ||
    (abs >= 1_000_000 && parseFloat((abs / 1_000_000).toFixed(1)) >= 1000)
  ) {
    return sign + `${parseFloat((abs / 1_000_000_000).toFixed(1))}B`;
  }
  if (
    abs >= 1_000_000 ||
    (abs >= 1_000 && parseFloat((abs / 1_000).toFixed(1)) >= 1000)
  ) {
    return sign + `${parseFloat((abs / 1_000_000).toFixed(1))}M`;
  }
  if (abs >= 1_000) {
    return sign + `${parseFloat((abs / 1_000).toFixed(1))}K`;
  }
  return sign + Math.round(abs).toString();
};

// Large icon size — new assets include outer glow so render bigger
const ICON_SIZE = 170;

const renderCyanFloat = (text: string) => (
  <LText
    gradientColours={CYAN_GRADIENT}
    gradientDirection="vertical"
    variant="Heading/H1/Bold"
  >
    {text}
  </LText>
);
const renderOrangeFloat = (text: string) => (
  <LText
    gradientColours={ORANGE_GRADIENT_NUMBER}
    gradientDirection="vertical"
    variant="Heading/H1/Bold"
  >
    {text}
  </LText>
);

// Streams Card (Left - Cyan gradient) — icon behind number
const StreamsCard = ({ totalStreams }: { totalStreams: number }) => {
  const prevStreams = usePrevious(totalStreams) ?? totalStreams;
  const delta = totalStreams - prevStreams;

  return (
    <Pulse trigger={totalStreams} intensity={1.04}>
      <LBox alignItems="center" justifyContent="center" marginTop="s2">
        {/* Large glowing icon — absolutely positioned behind */}
        <LBox
          style={{
            position: "absolute",
            top: -ICON_SIZE * 0.3,
          }}
        >
          <FloatingFeedback
            trigger={totalStreams}
            floatingText={`${delta >= 0 ? "+" : ""}${formatNumber(delta)}`}
            renderFloatingContent={renderCyanFloat}
            size={ICON_SIZE}
          >
            <FloatingImage
              source={require("@assets/stats/fair_plays.webp")}
              size={ICON_SIZE}
              phaseOffset={0}
              shadowColor={CYAN_GRADIENT[1]}
              opacity={0.75}
              baseDuration={2600}
            />
          </FloatingFeedback>
        </LBox>

        {/* Number — on top of icon */}
        <LBox
          style={{
            marginTop: ICON_SIZE * 0.325,
          }}
        >
          <LText
            fontFamily={fonts.extraBold}
            fontSize={scaleFont(40)}
            height={scaleFont(34)}
            lineHeight={scaleFont(40)}
            letterSpacing={-0.2}
            textAlign="center"
            gradientColours={[CYAN_GRADIENT[0], CYAN_GRADIENT[1]]}
            gradientDirection="vertical"
          >
            {formatNumber(totalStreams)}
          </LText>
        </LBox>
        <LText
          gradientColours={[CYAN_GRADIENT[1], CYAN_GRADIENT[0]]}
          gradientDirection="horizontal"
          textAlign="center"
          variant="Caption/C2/SemiBold"
          adjustsFontSizeToFit
          numberOfLines={1}
        >
          FAIR PLAYS
        </LText>
      </LBox>
    </Pulse>
  );
};

// Experiences Card (Right - Orange gradient) — icon behind number
const ExperiencesCard = ({ accesses }: { accesses: number }) => {
  const prevAccesses = usePrevious(accesses) ?? accesses;
  const delta = accesses - prevAccesses;

  return (
    <Pulse trigger={accesses} intensity={1.04}>
      <LBox alignItems="center" justifyContent="center" marginTop="s2">
        {/* Large glowing icon — absolutely positioned behind */}
        <LBox
          style={{
            position: "absolute",
            top: -ICON_SIZE * 0.3,
            opacity: 0.75,
          }}
        >
          <FloatingFeedback
            trigger={accesses}
            floatingText={`${delta >= 0 ? "+" : ""}${formatNumber(delta)}`}
            renderFloatingContent={renderOrangeFloat}
            size={ICON_SIZE}
          >
            <FloatingImage
              source={require("@assets/stats/experiences.webp")}
              size={ICON_SIZE}
              phaseOffset={0.35}
              shadowColor={ORANGE_GRADIENT_TEXT[1]}
              opacity={0.75}
              baseDuration={2800}
            />
          </FloatingFeedback>
        </LBox>

        {/* Number — on top of icon */}
        <LBox
          style={{
            marginTop: ICON_SIZE * 0.325,
          }}
        >
          <LText
            fontFamily={fonts.extraBold}
            fontSize={scaleFont(40)}
            height={scaleFont(34)}
            lineHeight={scaleFont(40)}
            letterSpacing={-0.2}
            textAlign="center"
            gradientColours={ORANGE_GRADIENT_NUMBER}
            gradientDirection="vertical"
          >
            {formatNumber(accesses)}
          </LText>
        </LBox>
        <LText
          variant="Caption/C2/SemiBold"
          textAlign="center"
          gradientColours={[ORANGE_GRADIENT_TEXT[1], ORANGE_GRADIENT_TEXT[0]]}
          gradientDirection="vertical"
        >
          EXPERIENCES
        </LText>
      </LBox>
    </Pulse>
  );
};

export const StatsTrio = ({ totalStreams, accesses }: StatsTrioProps) => {
  return (
    <LBox flexDirection="row" alignItems="flex-start">
      {/* Left column - Fair Plays */}
      <LBox flex={1} alignItems="center">
        <StreamsCard totalStreams={totalStreams} />
      </LBox>

      {/* Right column - Experiences */}
      <LBox flex={1} alignItems="center">
        <ExperiencesCard accesses={accesses} />
      </LBox>
    </LBox>
  );
};

export default StatsTrio;
